import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Sun, Star, Coffee, Music } from 'lucide-react';

const faqs = [
  {
    q: "What makes Rudragarh romantic for couples?",
    a: "It's the boutique scale — Rudragarh is a small property with personal service, not a large hotel where you're one of three hundred guests. Your private dunes mean no strangers walking through your sunset. The cave stay is genuinely one-of-a-kind — nowhere else in India offers a room quite like it. And there's a cultural depth here: camel rides, heritage architecture, live kitchen, traditional low-sitting hall — you experience Rajasthan together rather than just looking at it from behind a pool."
  },
  {
    q: "Are there activities for couples at Rudragarh?",
    a: "Yes, and they're the kind that work well as shared experiences. ATV rides together on the private dunes. Camel safari through desert terrain. Stargazing at night with no city light interference — the Thar sky is remarkable. Horse riding in the early morning. Cultural evenings with traditional music and dance. Open-air theatre under the stars. These aren't activities you do while your partner watches — they're things you do together and remember together."
  },
  {
    q: "Is Rudragarh suitable for anniversary celebrations?",
    a: "Yes — and the team can personalise arrangements for special occasions. Contact us in advance and mention your anniversary when booking. The property's small scale means we can give your celebration genuine attention rather than a template response. The cave stay and king suite both lend themselves beautifully to milestone evenings — and the private dunes make for an anniversary sunset that's impossible to replicate anywhere else."
  }
];

const highlights = [
  {
    icon: <Sun size={28} />,
    title: "Morning on the Dunes",
    desc: "The desert at dawn is a different world — cool air, long light, and complete quiet. Start your day on private dunes before the heat sets in."
  },
  {
    icon: <Coffee size={28} />,
    title: "Afternoon at the Pool",
    desc: "A desert pool is something rare. Afternoons at Rudragarh move slowly — a swim, a rest, the shade of the open-air terrace. No rush, no agenda."
  },
  {
    icon: <Music size={28} />,
    title: "Evenings of Culture & Theatre",
    desc: "As the sun goes down, the cultural program begins — traditional music, dance, and open-air theatre give the evening a ceremony that feels earned."
  },
  {
    icon: <Star size={28} />,
    title: "Nights Under the Desert Sky",
    desc: "Far from city light, the Thar sky at night is extraordinary. Stargazing here isn't an activity — it's an inevitability. Bring a blanket."
  }
];

const rooms = [
  {
    slug: "catacomb-suite",
    name: "The Catacomb Suite",
    price: 10000,
    guests: 4,
    category: "unique",
    desc: "Carved rock, lantern light, desert silence. The cave stay offers the most immersive, private, and genuinely unique experience at Rudragarh."
  },
  {
    slug: "cascade-cottages",
    name: "Cascade Cottages",
    price: 12000,
    guests: 2,
    category: "heritage",
    desc: "Birdsong, pond views, and heritage Rajasthani architecture. A quiet, charming space designed for two, with four standalone units available."
  },
  {
    slug: "cocoon",
    name: "The Cocoon",
    price: 4200,
    guests: 2,
    category: "heritage",
    desc: "Hand-crafted woodwork, artisan finishes, and a warmth that hotel rooms rarely achieve. An understated and beautiful room for couples."
  }
];

const Couples = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Couples Resort in Jaisalmer | Romantic Getaway at Rudragarh</title>
        <meta name="description" content="Escape to Rudragarh — Jaisalmer's most romantic resort for couples. Private sand dunes, cave stays, sunset views, stargazing, and boutique desert luxury near Sam Sand Dunes." />
        <meta property="og:title" content="Couples Resort in Jaisalmer | Romantic Getaway at Rudragarh" />
        <meta property="og:description" content="Escape to Rudragarh — Jaisalmer's most romantic resort for couples. Private sand dunes, cave stays, sunset views, stargazing, and boutique desert luxury near Sam Sand Dunes." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/for/couples" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Couples Resort in Jaisalmer | Rudragarh" />
        <meta name="twitter:description" content="Private dunes, cave stays, sunset views, stargazing — boutique desert luxury for couples near Sam Sand Dunes." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/for/couples" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pool-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Jaisalmer, Rajasthan</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              A Romantic Getaway in <span className="text-gold-500">Jaisalmer</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              From dunes at dawn to stargazing at midnight — Rudragarh is a place where days have a rhythm, evenings have ceremony, and nothing feels rushed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="overline">For Couples</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Two days at Rudragarh. <span className="text-gold-500">A week's worth of memory.</span>
            </h2>
            <p className="text-foreground/70 leading-relaxed">
              A romantic getaway doesn't have to mean a spa weekend or a hill station in the rain. The desert offers something different — space, silence, and a scale that makes you feel genuinely far away from everything. Rudragarh is set on private land near Sam Sand Dunes, and the pace of a day here is its own kind of luxury.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              Mornings begin on private dunes before the heat arrives. Afternoons slow down at the pool, or exploring the natural pond and waterfall tucked into the property. As evening sets in, the cultural program begins — traditional music, dance, open-air theatre under a darkening sky. And then the night: the Thar with no light pollution, stars that actually make sense of the word "stargazing."
            </p>
            <p className="text-foreground/70 leading-relaxed">
              This rhythm — unhurried, experiential, genuinely together — is what makes Rudragarh work for couples. Not a package, not a programme. Just a place that gives you the conditions to actually switch off.
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Your Days at Rudragarh</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              A Day That Has <span className="text-gold-500">a Rhythm</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{h.icon}</div>
                <h3 className="font-heading text-xl">{h.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Where You'll Stay</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Rooms Worth <span className="text-gold-500">Coming Back For</span>
            </h2>
            <p className="text-foreground/60 mt-4 max-w-xl mx-auto">
              A cave in the desert rock, a heritage cottage with pond views, or an artisan wooden room — each one is its own kind of romance.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All Rooms</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Common Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Couples at <span className="text-gold-500">Rudragarh</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pool-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Plan Your Getaway</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            The Thar is Waiting
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            A desert resort that gives you privacy, pace, and a setting that turns two days into a memory that stays. Book your couple's stay at Rudragarh.
          </p>
          <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
            Book Your Stay
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Couples;
